import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';

interface TermsPageProps {
  onBack: () => void;
}

export const TermsPage: React.FC<TermsPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen relative">
      {/* Back Button */}
      <div className="absolute top-8 left-8 z-20">
        <button
          onClick={onBack}
          className="inline-flex items-center space-x-2 text-white hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Quellect</span>
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card p-8 sm:p-12"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
              >
                Terms of Use
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-lg text-gray-600"
              >
                for Quellect.ai
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-4 text-sm text-gray-500 space-y-1"
              >
                <p>Effective date: August 31, 2025</p>
                <p>Contact: info@quellect.ai</p>
              </motion.div>
            </div>

            {/* Terms Content */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="prose prose-lg max-w-none text-gray-700"
            >
              <p className="text-lg mb-8">
                Welcome to Quellect.ai! By joining our waitlist or using this site, you agree to these Terms.
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Purpose of the Waitlist</h2>
                  <p>
                    The Quellect waitlist allows interested users to register their interest in our upcoming product. 
                    Joining the waitlist does not guarantee access to the product, future features, or pricing.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Eligibility</h2>
                  <p>
                    You must be at least 16 years old to join the waitlist. By signing up, you confirm you meet this requirement.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Acceptable Use</h2>
                  <p>You agree not to:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Use automated tools or bots to spam the waitlist.</li>
                    <li>Provide false or misleading information.</li>
                    <li>Attempt to interfere with the security or operation of the site.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Intellectual Property</h2>
                  <p>
                    All content on this site (brand, copy, design) belongs to Quellect.ai. You may not copy, distribute, 
                    or use it without permission.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Disclaimer of Warranties</h2>
                  <p>
                    The waitlist is provided "as is." We do not guarantee uninterrupted service, error-free operation, 
                    or future product availability.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Limitation of Liability</h2>
                  <p>
                    To the fullest extent permitted by law, Quellect.ai and its founders are not liable for any damages 
                    arising out of your use of the waitlist.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Termination</h2>
                  <p>
                    We reserve the right to remove any signup from the waitlist at our discretion.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Governing Law</h2>
                  <p>
                    These Terms are governed by the laws of the State of Delaware, United States (or your local consumer 
                    protection laws if stronger).
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Changes to These Terms</h2>
                  <p>
                    We may update these Terms from time to time. Continued use of the site after updates means you accept 
                    the revised Terms.
                  </p>
                </section>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
